import React, {useCallback, useMemo} from 'react';
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {useEmployeeVisitStore} from "../../api/EmployeeVisitStore";
import {
    EmployeeVisitReportFilter,
    employeeVisitReportFilterSerializer,
    filterEmployeeVisitReport,
    filterEmployeeVisitReportByDates
} from "./EmployeeVisitReportFilter";
import {VisitHeatMap} from "./VisitHeatMap";
import {Neighbourhood} from "../../api/Model";
import {useFilter} from "../../util/FilterSerializer";
import {RemoteValue} from "../../util/RemoteValue";
import {useEmployeeNeighbourhoodStore} from "../../api/EmployeeNeighbourhoodStore";
import {Button, Skeleton, Stack} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {useMessage} from "../../util/actions/Message";
import {defaultEmployeeVisitReportName, downloadEmployeeVisitCsv} from "./ExportEmployeeVisitCsv";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {ExportForDatesDialog, ExportForDatesForm} from "./ExportForDatesDialog";
import {useEmployeeMapStore} from "../../api/EmployeeMapStore";
import {SlowContent} from "../../components/loading-data/SlowContent";
import {ResidenceModel} from "../../api/ResidenceStore";
import {VisitDataCoverageWarning} from "./VisitDataCoverageWarning";

export default function EmployeeVisitReportPage({neighbourhoods, residenceModel}: {
    neighbourhoods: Neighbourhood[],
    residenceModel: ResidenceModel,
}) {
    const {remoteValue: unfiltered} = useEmployeeVisitStore();
    const filterSerializer = useMemo(
        () => employeeVisitReportFilterSerializer(neighbourhoods, residenceModel),
        [neighbourhoods, residenceModel]
    );
    const [filter, setFilter] = useFilter(filterSerializer);

    const employeeNeighbourhoods = useEmployeeNeighbourhoodStore(state => state.remoteValue);
    const employeeMapRV = useEmployeeMapStore(state => state.remoteValue);
    const filtered = useMemo(
        () =>
            RemoteValue.combine({unfiltered, employeeNeighbourhoods, employeeMap: employeeMapRV})
                .map(({unfiltered, employeeNeighbourhoods, employeeMap}) =>
                    filterEmployeeVisitReport(unfiltered, employeeNeighbourhoods, employeeMap, filter)
                ),
        [filter, unfiltered, employeeNeighbourhoods, employeeMapRV],
    );
    const message = useMessage();
    const handleExportCsv = useCallback(async () => {
        const sheet = await filtered.value;
        const employeeMap = await employeeMapRV.value;
        if (sheet.rows.length <= 0) {
            message.error('No employees found to export.');
        } else if (sheet.dates.length <= 0) {
            message.error('No visits found for the selected dates.');
        } else {
            downloadEmployeeVisitCsv(employeeMap, sheet);
        }
    }, [filtered, employeeMapRV, message]);

    const handleExportForDatesCsv = useFormDialogAction<ExportForDatesForm, []>({
        title: 'Export for Dates',
        description: 'Select working days to include in the CSV report.',
        submitButtonText: 'Export',
        renderForm: ExportForDatesDialog,
        action: async (openDialog) => {
            const sheet = await filtered.value;
            const employeeMap = await employeeMapRV.value;
            if (sheet.rows.length <= 0) {
                message.error('No employees found to export.');
                return;
            }
            if (sheet.dates.length <= 0) {
                message.error('Empty date range. Please select at least one working day to export.');
                return;
            }

            openDialog({
                initialValue: {
                    reportName: defaultEmployeeVisitReportName(),
                    selectedDates: new Set(),
                    sheet,
                },
                onSubmit: async (form) => {
                    downloadEmployeeVisitCsv(
                        employeeMap,
                        filterEmployeeVisitReportByDates(sheet, form.selectedDates),
                        form.reportName
                    );
                },
                successMessage: 'CSV report exported successfully.',
                errorMessage: 'Failed to export CSV report.',
            });
        }
    }, [filtered, employeeMapRV, message]);

    return (
        <Page
            header="Employee Visits"
            counter={<StorePageCount totalValue={unfiltered} value={filtered}/>}
            actions={
                <Stack direction="row" gap={2}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon/>}
                        onClick={handleExportCsv}
                    >
                        Export All
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon/>}
                        onClick={handleExportForDatesCsv}
                    >
                        Export for Dates
                    </Button>
                </Stack>
            }
            filter={<EmployeeVisitReportFilter filter={filter} setFilter={setFilter}/>}
        >
            <LoadingData
                totalValue={unfiltered}
                value={filtered}
                emptyMessage="No employee visit data available."
                noMatchMessage="No employee visits match the current filters. Try adjusting your filter criteria."
                defaultErrorMessage="Failed to load employee visits data. Please try again later."
            >{resolved => (
                <>
                    <VisitDataCoverageWarning
                        dates={resolved.dates}
                        dateRange={filter.dateRange}
                    />
                    {resolved.dates.length > 0 && (
                        <SlowContent
                            loading={<Skeleton height="800px" variant="rectangular"/>}
                        >
                            <VisitHeatMap data={resolved}/>
                        </SlowContent>
                    )}
                </>
            )}</LoadingData>
        </Page>
    );
}
